import { FC } from "react";
import {
  CattleFeed,
  TargetAnimal,
} from "../../../../models/Intervention/CattleIntervention";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { useFormContext } from "react-hook-form";
import { enumToOptionsArrayWithTranslatedStrings } from "../../../../utils/obj-utils";
import { FeedStatus } from "../../../../../../graphql/generated/blonk/cattle";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { FormType } from "../../common";
import RowSelectWithMetricsV2 from "../../../../../modules/Farms/Intervention/RowSelectWithMetricsV2";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { StageType } from "../../../../../../graphql/types";
import { CattleFeed as CattleBaselineFeed } from "../../../../models/Baseline/CattleBaseline";

import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import RowControlledTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";

interface IBeefFeedDietCharacterisationProp {
  formType: FormType;
  fieldItemPrefix: string;
  baselineStageData: CattleBaselineFeed;
  currentFormValues: CattleFeed | undefined | null;
  target?: TargetAnimal;
  stageType: StageType;
  isDietEdit: boolean;
  setIsDietEdit: (val: boolean) => void;
}

const BeefFeedDietCharacterisation: FC<IBeefFeedDietCharacterisationProp> = ({
  formType,
  fieldItemPrefix,
  baselineStageData,
  target,
  stageType,
  currentFormValues,
  isDietEdit,
  setIsDietEdit,
}) => {
  const intl = useIntl();
  const classes = processAndStageStylesV2() as CSSClassesList;
  const fc = useFormContext<BeefIntervention>();
  const isCalvesTarget = target === TargetAnimal.CALVES;

  const dietCharacterisationOptions = () =>
    enumToOptionsArrayWithTranslatedStrings(
      FeedStatus,
      intl,
      "SUSTELL.STAGE.BEEF.FEED"
    );

  const cancelEditionHandler = () => {
    fc.setValue(
      `${fieldItemPrefix}.dietCharacterisation`,
      currentFormValues?.dietCharacterisation ?? ""
    );
    if (isCalvesTarget) {
      fc.setValue(
        `${fieldItemPrefix}.averageMilkFat`,
        currentFormValues?.averageMilkFat ?? ""
      );
    }
    setIsDietEdit(false);
  };

  const getTooltip = () => {
    if (target) {
      return intl.formatMessage({
        id: `SUSTELL.STAGE.BEEF.FEED.${stageType}.DIET_CHARACTERISATION.${target.toLocaleUpperCase()}.TOOLTIP`,
      });
    }
    return intl.formatMessage({
      id: `SUSTELL.STAGE.BEEF.FEED.${stageType}.DIET_CHARACTERISATION.${TargetAnimal.COWS.toLocaleUpperCase()}.TOOLTIP`,
    });
  };

  const renderMilkFat = () => {
    if (!isCalvesTarget) return null;

    const isViewOnly = !isDietEdit;

    const milkFatFieldName = `${fieldItemPrefix}.averageMilkFat`;
    const value = fc.getValues(milkFatFieldName);
    const hasAverageMilkFat =
      value && value !== baselineStageData.averageMilkFat;

    if (isViewOnly) {
      if (hasAverageMilkFat) {
        return (
          <RowTextFieldWithMetrics
            name={milkFatFieldName}
            label={intl.formatMessage({
              id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT",
            })}
            tooltip={intl.formatMessage({
              id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT.TOOLTIP",
            })}
            type="number"
            metricUnit=""
            baseline={baselineStageData.averageMilkFat ?? ""}
            disabled
            displayTypeSelect={false}
          />
        );
      }

      return (
        <ReactHookDsmInput
          name={`${fieldItemPrefix}.originalAverageMilkFat`}
          label={intl.formatMessage({
            id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT",
          })}
          tooltip={intl.formatMessage({
            id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT.TOOLTIP",
          })}
          type="number"
          adornment={intl.formatMessage({
            id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT.ADORNMENT",
          })}
          defaultValue={baselineStageData.averageMilkFat ?? ""}
          disabled
        />
      );
    }

    return (
      <RowControlledTextFieldWithMetrics
        name={milkFatFieldName}
        label={intl.formatMessage({
          id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT",
        })}
        tooltip={intl.formatMessage({
          id: "SUSTELL.STAGE.BEEF.FEED.AVERAGE_MILK_FAT.TOOLTIP",
        })}
        type="number"
        metricUnit=""
        baseline={baselineStageData.averageMilkFat}
        disabled={isViewOnly}
        displayTypeSelect
      />
    );
  };
  return (
    <DsmGrid
      className={
        isCalvesTarget
          ? classes.dsmGridTwoColumn
          : classes.dsmGridOneColumn
      }
      style={{
        ...(isCalvesTarget && {
          gridTemplateColumns: "1fr auto",
        }),
        rowGap: "0px"
      }}
    >
      <RowSelectWithMetricsV2
        name={`${fieldItemPrefix}.dietCharacterisation`}
        label={`${intl.formatMessage({
          id: "SUSTELL.STAGE.DAIRY.FEED.DIET_CHARACTERISATION",
        })}`}
        disabled={!isDietEdit}
        baseline={baselineStageData.dietCharacterisation}
        options={dietCharacterisationOptions()}
        tooltip={getTooltip()}
        useStyle={!isDietEdit}
        originalLabel={!!(
          isDietEdit ||
          fc.getValues(`${fieldItemPrefix}.dietCharacterisation`) &&
          fc.getValues(`${fieldItemPrefix}.dietCharacterisation`) !==
          baselineStageData.dietCharacterisation
        )}
      />
      {renderMilkFat()}
      {formType !== FormType.View &&
        <EditModeButtons
          isEditing={isDietEdit}
          onEdit={() => setIsDietEdit(true)}
          onCancel={cancelEditionHandler}
        />}
    </DsmGrid>
  );
};

BeefFeedDietCharacterisation.defaultProps = {
  target: undefined,
};

export default BeefFeedDietCharacterisation;
